import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import i18n from '../i18n';

// ============== Types ==============

export interface AppSettings {
  theme: 'light' | 'dark' | 'system';
  fontSize: 'small' | 'medium' | 'large' | 'xlarge';
  language: 'ko' | 'en' | 'ja' | 'zh';
  compactMode: boolean;
  autoSave: boolean;
  showTips: boolean;
}

interface SettingsContextType {
  settings: AppSettings;
  updateSettings: (newSettings: Partial<AppSettings>) => void;
  saveToBackend: () => Promise<void>;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
}

// ============== Constants ==============

const STORAGE_KEY = 'proposalcraft_settings';

const DEFAULT_SETTINGS: AppSettings = {
  theme: 'light',
  fontSize: 'medium',
  language: 'ko',
  compactMode: false,
  autoSave: true,
  showTips: true,
};

const FONT_SIZE_MAP = {
  small: '14px',
  medium: '16px',
  large: '18px',
  xlarge: '20px',
};

const SPACING_MAP = {
  compact: '0.75rem',
  normal: '1rem',
};

// ============== Context ==============

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// ============== Helper Functions ==============

const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001/api';
  }
  return `${protocol}//${hostname}:8001/api`;
};

const applyTheme = (theme: AppSettings['theme']) => {
  const root = document.documentElement;
  
  if (theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
  } else {
    root.setAttribute('data-theme', theme);
  }
};

const applyFontSize = (fontSize: AppSettings['fontSize']) => {
  const root = document.documentElement;
  root.setAttribute('data-font-size', fontSize);
  root.style.setProperty('--font-size-base', FONT_SIZE_MAP[fontSize]);
};

const applyCompactMode = (compactMode: boolean) => {
  const root = document.documentElement;
  root.setAttribute('data-compact', compactMode ? 'true' : 'false');
  root.style.setProperty('--spacing-base', compactMode ? SPACING_MAP.compact : SPACING_MAP.normal);
};

const applyLanguage = (language: AppSettings['language']) => {
  document.documentElement.setAttribute('lang', language);
  // Sync the i18next instance whenever the language setting is applied.
  // Only 'ko' and 'en' have translation resources; other values fall back to 'ko'.
  if (i18n.language !== language) {
    i18n.changeLanguage(language);
  }
};

const applyAllSettings = (settings: AppSettings) => {
  applyTheme(settings.theme);
  applyFontSize(settings.fontSize);
  applyCompactMode(settings.compactMode);
  applyLanguage(settings.language);
};

// ============== Provider ==============

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // localStorage에서 설정 로드
  useEffect(() => {
    const loadSettings = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as Partial<AppSettings>;
          const merged = { ...DEFAULT_SETTINGS, ...parsed };
          setSettings(merged);
          applyAllSettings(merged);
        } else {
          applyAllSettings(DEFAULT_SETTINGS);
        }
      } catch (error) {
        console.error('[SettingsContext] Failed to load settings from localStorage:', error);
        applyAllSettings(DEFAULT_SETTINGS);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  // 시스템 테마 변경 감지
  useEffect(() => {
    if (settings.theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      applyTheme('system');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [settings.theme]);

  // 설정 업데이트
  const updateSettings = useCallback((newSettings: Partial<AppSettings>) => {
    setSettings((prev) => {
      const updated = { ...prev, ...newSettings };
      
      // localStorage에 저장
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('[SettingsContext] Failed to save settings to localStorage:', error);
      }
      
      // CSS 변수 적용
      applyAllSettings(updated);
      
      return updated;
    });
  }, []);

  // 백엔드에 설정 저장
  const saveToBackend = useCallback(async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.warn('[SettingsContext] No auth token, skipping backend save');
        return;
      }

      const response = await fetch(`${getApiBaseUrl()}/auth/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        setLastSaved(new Date());
        console.log('[SettingsContext] Settings saved to backend');
      } else {
        console.error('[SettingsContext] Failed to save settings to backend:', response.status);
      }
    } catch (error) {
      console.error('[SettingsContext] Error saving settings to backend:', error);
    } finally {
      setIsSaving(false);
    }
  }, [settings]);

  // 로그인 후 백엔드에서 설정 불러오기
  useEffect(() => {
    const loadFromBackend = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        const response = await fetch(`${getApiBaseUrl()}/auth/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.settings && Object.keys(data.settings).length > 0) {
            const merged = { ...DEFAULT_SETTINGS, ...data.settings };
            setSettings(merged);
            applyAllSettings(merged);
            // localStorage도 업데이트
            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
            console.log('[SettingsContext] Settings loaded from backend');
          }
        }
      } catch (error) {
        console.error('[SettingsContext] Error loading settings from backend:', error);
      }
    };

    loadFromBackend();
  }, []);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        updateSettings,
        saveToBackend,
        isLoading,
        isSaving,
        lastSaved,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

// ============== Hook ==============

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;
